import React, { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { useSelector } from 'react-redux';
import {
    SupportedLocale,
    FALLBACK_LOCALE,
    isSupportedLocale,
} from './config';
import { loadLocaleMessages, MessageMap } from './loader';
import { handleMissingKey } from './missingKey';
import { pseudoizeMessages, isPseudoLocale } from './pseudo';
import { setRuntimeMessages } from './t';

interface Props {
    children: React.ReactNode;
    /** Locale module JSON files to preload. Login page uses ['login']; contact uses ['contact'], etc. */
    modules?: string[];
}

const I18nProvider: React.FC<Props> = ({ children, modules = ['common'] }) => {
    const storedLocale = useSelector((s: any) => s?.app?.locale);

    const requested: string = typeof storedLocale === 'string' ? storedLocale : FALLBACK_LOCALE;
    const resolved: SupportedLocale = isSupportedLocale(requested)
        ? requested
        : FALLBACK_LOCALE;

    const [messages, setMessages] = useState<MessageMap>({});

    useEffect(() => {
        let cancelled = false;

        loadLocaleMessages(resolved, modules).then((loaded) => {
            if (cancelled) return;
            const final = isPseudoLocale(requested) ? pseudoizeMessages(loaded) : loaded;
            setMessages(final);
            // Snapshot for non-React callers (action handlers, toast callers) so t() stays in sync.
            setRuntimeMessages(resolved, final);
        });

        return () => { cancelled = true; };
        // modules is an array prop — stringify to avoid identity churn.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requested, resolved, modules.join(',')]);

    return (
        <IntlProvider
            locale={resolved}
            defaultLocale={FALLBACK_LOCALE}
            messages={messages}
            onError={handleMissingKey as any}
        >
            {children}
        </IntlProvider>
    );
};

export default I18nProvider;
